import {
  type WorkspaceBrandingResource,
  type WorkspaceConfigResource,
} from '@/features/iam/api/resources/workspace-config'
import { WorkspaceConfig } from '@/features/iam/workspaces/workspace-config'
import { WorkspaceBranding } from '@/features/iam/workspaces/workspace-branding'
import { type WorkspaceResource } from '@/features/iam/api/resources/workspaces'
import { type WorkspaceProps } from '@/features/iam/workspaces/Workspace'
import { match } from '@/utils/pattern-match-util'

export function mapWorkspaceConfigResourceToProps(
  resource: WorkspaceConfigResource,
): WorkspaceConfig {
  return {
    billing: {
      enabled: resource.billing?.enabled ?? false,
      coupons: {
        enabled: resource.billing?.['coupons.enabled'] ?? false,
        referral: {
          enabled: resource.billing?.['coupons.referral.enabled'] ?? false,
          amount: resource.billing?.['coupons.referral.amount'] ?? 0,
        },
      },
      stripe_pk: resource.billing?.stripe_pk,
    },
    calling: {
      enabled: resource.calling_config?.enabled ?? false,
    },
    jobs: {
      videoChat: resource.job_config?.job_video_chat ?? false,
      progressionFlow: resource.job_config?.job_progression_flow ?? false,
      enableChatInInfoGathering:
        resource.job_config?.enable_chat_in_info_gathering ?? false,
    },
    signup: {
      inviteOnly: resource.signup?.invite_only ?? false,
      connections:
        resource.signup?.connections.map((conn) => ({
          type: conn.type,
          enabled: conn.enabled,
        })) ?? [],
      title: resource.signup?.title ?? WorkspaceConfig.default.signup.title,
      description:
        resource.signup?.description ??
        WorkspaceConfig.default.signup.description,
      logoutUrl: resource.signup?.logout_url,
      federatedLogout: resource.signup?.federated_logout ?? false,
      externalProfiles: resource.signup?.external_profiles ?? false,
    },
    privacyPolicy: {
      url: resource.pp?.url ?? WorkspaceConfig.default.privacyPolicy.url,
      enabled: resource.pp?.enabled ?? false,
    },
    termsOfService: {
      // We're also checking for whether it's enabled specifically on
      // Customer Portal (that's us!)
      enabled: !!resource.tos && !resource.tos?.hide_on_customer_portal,
      showOnLogin: resource.tos?.show_on_login ?? false,
      customTermsEnabled: Boolean(resource.tos?.custom_source),
    },
    email: {
      supportEmail:
        resource.email_config?.support_email ??
        WorkspaceConfig.default.email.supportEmail,
    },
    externalCustomer: {
      enabled: resource.external_customers_config?.enabled ?? false,
      allowRequestingMagicLinks:
        resource.external_customers_config?.allow_requesting_magic_links ??
        false,
    },
    publicApi: {
      connection: resource.public_api_config?.connection ?? '',
      magicLinkEnabled: resource.public_api_config?.signed_url_enabled ?? false,
    },
    combinedPricing: {
      jobScopePromptClientApproval:
        resource.combined_pricing?.job_scope_prompt_client_approval ?? false,
    },
    jobDetails: {
      contactSupportEnabled:
        resource.job_details_config?.['contact_support.enabled'] ?? true,
    },
    section7216: {
      enabled: resource.section_7216_config?.enabled ?? false,
    },
    hubspotChat: {
      enabled: resource.hubspot_chat_config?.customer_portal_enabled ?? false,
    },
    callCenter: {
      enabled: resource.call_center_config?.enabled ?? false,
      phoneNumber: resource.call_center_config?.phone_number ?? '',
      totalRequiredQuestions:
        resource.call_center_config?.total_required_questions ?? 0,
      recommendAssistedOption:
        resource.call_center_config?.recommend_assisted_option ?? true,
      enableCallOptionsModal:
        resource.call_center_config?.enable_call_options_modal ?? true,
    },
    measureOne: {
      enabled: resource.measure_one_config?.enabled ?? false,
    },
    customerPortal: {
      showServiceTypeInfo:
        resource.customer_portal?.show_service_type_info ?? false,
    },
    agents: {
      enabled: resource.agents?.enabled ?? false,
    },
  }
}

export function mapWorkspaceResourceToProps(
  resource: WorkspaceResource,
): WorkspaceProps {
  return {
    id: resource.id,
    name: resource.name,
    slug: resource.slug,
    branding: mapWorkspaceBrandingResourceToProps(resource.branding),
    config: mapWorkspaceConfigResourceToProps(resource.config),
    archivalStatus: resource.archival_status,
    classification: match(resource.classification, {
      B2C: 'B2C',
      B2B: 'B2B',
      B2B2C: 'B2B2C',
      B2E: 'B2E',
      DEMO: 'DEMO',
      TXF: 'TXF',
    }),
  }
}

function mapWorkspaceBrandingResourceToProps(
  resource: WorkspaceBrandingResource,
): WorkspaceBranding {
  return {
    corporateMode: resource.corporate.enabled,
    logos: {
      primary:
        resource.logos.primary || WorkspaceBranding.default.logos.primary,
      primaryWhite:
        resource.logos.primary_white ||
        WorkspaceBranding.default.logos.primaryWhite,
      icon: resource.logos.icon || WorkspaceBranding.default.logos.icon,
      iconWhite:
        resource.logos.icon_white || WorkspaceBranding.default.logos.iconWhite,
      systemRobotAvatar:
        resource.system_robot.image ||
        WorkspaceBranding.default.logos.systemRobotAvatar,
    },
    colors: {
      primary:
        resource.colors.primary || WorkspaceBranding.default.colors.primary,
      primaryDark: resource.colors.primary_dark || null,
    },
  }
}
